import { collection, doc, getDocFromServer, getDocsFromServer, runTransaction, Transaction } from "firebase/firestore";
import { isEqual } from "lodash";
import { firestore as db } from "lib/firebase/firebase";
import { dataObjectWithoutUndefinedFields } from "utils/dataManipulation";
import { FirestoreInventoryItemProps, FirestoreInventoryMeasurementProps } from "types/auth";
import { approvalStatus, assertInventoryCurrent, inventoryLocked, nextApprovalStatus, preserveApproval } from "utils/inventoryLifecycle";

import type { InventoryEditorSnapshot, SaveInventoryEditorProps } from "types/mapData";

export async function getInventoryEditorSnapshot(inventoryId: string): Promise<InventoryEditorSnapshot> {
  // Read the parent revision first so a concurrent measurement change invalidates the save.
  const parent = await getDocFromServer(doc(db, "inventories", inventoryId));
  if (!parent.exists()) throw new Error("inventory-stale");
  const measurements = await getDocsFromServer(collection(db, "inventories", inventoryId, "measurements"));
  return {
    inventory: parent.data() as FirestoreInventoryItemProps,
    measurements: measurements.docs.map((item) => item.data() as FirestoreInventoryMeasurementProps),
  };
}

/** Application-level guard; Firestore rules are deliberately unchanged. */
export async function withEditableInventory<T>(inventoryId: string, edit: (tx: Transaction) => Promise<T>) {
  return runTransaction(db, async (tx) => {
    const current = await tx.get(doc(db, "inventories", inventoryId));
    if (!current.exists() || inventoryLocked(current.data())) throw new Error("inventory-stale");
    return edit(tx);
  });
}

/** One commit for stack, measurements and submission. No activity snapshot is taken. */
export async function saveInventoryEditor({ expected, data, measurements = [], expectedMeasurements = [], actor, action, note }: SaveInventoryEditorProps, assertCanWrite: () => void = () => {}) {
  assertCanWrite();
  const ref = doc(db, "inventories", expected.id);
  const measurementRefs = measurements.map((m) => m.id
    ? doc(db, "inventories", expected.id, "measurements", m.id)
    : doc(collection(db, "inventories", expected.id, "measurements")));
  const removedMeasurements = data ? expectedMeasurements.filter((m) => !measurements.some((edited) => edited.id === m.id)) : [];
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    if (!snapshot.exists()) throw new Error("inventory-stale");
    const current = snapshot.data() as FirestoreInventoryItemProps;
    assertInventoryCurrent(expected, current);
    const locked = inventoryLocked(current);
    if (locked && (data || measurements.length)) throw new Error("inventory-stale");
    // Existing measurements can be edited outside this dialog. Check them before writing.
    for (let i = 0; i < measurements.length; i++) {
      if (!measurements[i].id) continue;
      const saved = await tx.get(measurementRefs[i]);
      const baseline = expectedMeasurements.find((m) => m.id === measurements[i].id);
      if (!saved.exists() || !baseline || saved.data().lastModifiedDate !== baseline.lastModifiedDate) {
        throw new Error("inventory-stale");
      }
    }
    for (const baseline of removedMeasurements) {
      const saved = await tx.get(doc(db, "inventories", expected.id, "measurements", baseline.id));
      if (!saved.exists() || saved.data().lastModifiedDate !== baseline.lastModifiedDate) throw new Error("inventory-stale");
    }
    const date = new Date().toISOString();
    const edited: any = data ? dataObjectWithoutUndefinedFields(data) : {};
    if (data && Object.prototype.hasOwnProperty.call(data, "buyer")) {
      edited.buyer = preserveApproval(data.buyer, current.buyer);
    }
    const update: any = Object.fromEntries(Object.entries(edited).filter(([key, value]) =>
      !isEqual(value, (expected as any)[key])));
    if (action) {
      const status = nextApprovalStatus(current, action);
      const buyer = update.buyer ?? current.buyer;
      if (!buyer?.id) throw new Error("inventory-stale");
      update.buyer = {
        ...buyer,
        ...(note !== undefined ? { note } : {}),
        releasedToBuyer: status === "pending" || status === "approved",
        statusChange: { status, user: { id: actor.id, name: actor.name }, date },
      };
    } else if (!data && note !== undefined && approvalStatus(current) === "pending") {
      update.buyer = { ...current.buyer, note };
    } else if (locked) {
      throw new Error("inventory-stale");
    }
    assertCanWrite();
    tx.update(ref, { ...update, lastModifiedDate: date, lastModifiedBy: actor });
    measurements.forEach((measurement, i) => {
      const values = {
        ...dataObjectWithoutUndefinedFields(measurement),
        inventoryId: expected.id,
        lastModifiedDate: date,
        lastModifiedBy: actor,
      };
      if (measurement.id) tx.update(measurementRefs[i], values);
      else tx.set(measurementRefs[i], { ...values, id: measurementRefs[i].id, creationDate: date, status: "current" });
    });
    removedMeasurements.forEach((measurement) => tx.delete(doc(db, "inventories", expected.id, "measurements", measurement.id)));
  });
}
